if (!customElements.get('product-kits')) {
  customElements.define(
    'product-kits',
    class ProductKits extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.tabButtons = this.querySelectorAll('[data-product-kits-tab]');
        this.tabPanels = this.querySelectorAll('[data-product-kits-panel]');
        this.backgrounds = this.querySelectorAll('[data-product-kits-background]');
        this.mediaButton = this.querySelector('[data-product-kits-media-toggle]');
        this.inner = this.querySelector('.product-kits__inner');
        this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.dragController = new AbortController();

        this.boundOnTabClick = this.onTabClick.bind(this);
        this.boundOnTabKeydown = this.onTabKeydown.bind(this);
        this.boundOnMediaToggle = this.onMediaToggle.bind(this);
        this.boundOnBlockSelect = this.onBlockSelect.bind(this);
        this.boundOnVisibility = this.onVisibilityChange.bind(this);
        this.boundSyncInnerHeight = this.syncInnerHeight.bind(this);
        this.onWindowResize = typeof debounce === 'function' ? debounce(this.boundSyncInnerHeight, 150) : this.boundSyncInnerHeight;

        this.tabButtons.forEach((button) => {
          button.addEventListener('click', this.boundOnTabClick);
          button.addEventListener('keydown', this.boundOnTabKeydown);
        });

        if (this.mediaButton) {
          this.mediaButton.addEventListener('click', this.boundOnMediaToggle);
        }

        document.addEventListener('shopify:block:select', this.boundOnBlockSelect);
        document.addEventListener('visibilitychange', this.boundOnVisibility);
        window.addEventListener('resize', this.onWindowResize);

        this.querySelectorAll('.product-kits__slider-list').forEach((slider) => {
          this.enableMouseDrag(slider);
        });

        this.syncMedia();
        requestAnimationFrame(() => this.syncInnerHeight());
        if (document.readyState !== 'complete') {
          window.addEventListener('load', this.boundSyncInnerHeight);
        }
      }

      disconnectedCallback() {
        this.tabButtons.forEach((button) => {
          button.removeEventListener('click', this.boundOnTabClick);
          button.removeEventListener('keydown', this.boundOnTabKeydown);
        });

        if (this.mediaButton) {
          this.mediaButton.removeEventListener('click', this.boundOnMediaToggle);
        }

        document.removeEventListener('shopify:block:select', this.boundOnBlockSelect);
        document.removeEventListener('visibilitychange', this.boundOnVisibility);
        window.removeEventListener('resize', this.onWindowResize);
        window.removeEventListener('load', this.boundSyncInnerHeight);
        this.dragController?.abort();
        this.initialized = false;
      }

      syncInnerHeight() {
        if (!this.inner || !this.tabPanels.length || this.isSyncingHeight) return;

        this.isSyncingHeight = true;

        const activePanel = Array.from(this.tabPanels).find((panel) => !panel.hidden) || this.tabPanels[0];

        this.inner.style.visibility = 'hidden';
        this.inner.style.minHeight = '';

        this.tabPanels.forEach((panel) => {
          panel.hidden = true;
        });

        let maxHeight = 0;

        this.tabPanels.forEach((panel) => {
          panel.hidden = false;
          const slider = panel.querySelector('slider-component');
          if (slider && typeof slider.resetPages === 'function') {
            slider.resetPages();
          }
          maxHeight = Math.max(maxHeight, this.inner.offsetHeight);
          panel.hidden = true;
        });

        this.tabPanels.forEach((panel) => {
          panel.hidden = panel !== activePanel;
        });

        const activeSlider = activePanel.querySelector('slider-component');
        if (activeSlider && typeof activeSlider.resetPages === 'function') {
          activeSlider.resetPages();
        }

        this.inner.style.minHeight = `${maxHeight}px`;
        this.inner.style.visibility = '';
        this.isSyncingHeight = false;
      }

      enableMouseDrag(slider) {
        let isPointerDown = false;
        let hasDragged = false;
        let startX = 0;
        let startScroll = 0;
        const dragThreshold = 6;
        const { signal } = this.dragController;

        slider.addEventListener(
          'pointerdown',
          (event) => {
            if (event.pointerType === 'touch' || event.button !== 0) return;
            isPointerDown = true;
            hasDragged = false;
            startX = event.clientX;
            startScroll = slider.scrollLeft;
            slider.setPointerCapture(event.pointerId);
          },
          { signal }
        );

        slider.addEventListener(
          'pointermove',
          (event) => {
            if (!isPointerDown) return;
            const delta = event.clientX - startX;
            if (!hasDragged && Math.abs(delta) < dragThreshold) return;
            hasDragged = true;
            slider.classList.add('is-dragging');
            event.preventDefault();
            slider.scrollLeft = startScroll - delta;
          },
          { signal, passive: false }
        );

        const stopDrag = (event) => {
          if (!isPointerDown) return;
          isPointerDown = false;
          slider.classList.remove('is-dragging');
          if (slider.hasPointerCapture(event.pointerId)) {
            slider.releasePointerCapture(event.pointerId);
          }
        };

        slider.addEventListener('pointerup', stopDrag, { signal });
        slider.addEventListener('pointercancel', stopDrag, { signal });
        slider.addEventListener(
          'lostpointercapture',
          () => {
            isPointerDown = false;
            slider.classList.remove('is-dragging');
          },
          { signal }
        );

        slider.addEventListener(
          'click',
          (event) => {
            if (!hasDragged) return;
            event.preventDefault();
            event.stopPropagation();
            hasDragged = false;
          },
          { capture: true, signal }
        );
      }

      onTabClick(event) {
        event.preventDefault();
        this.activateTab(event.currentTarget.dataset.buttonIndex);
      }

      onTabKeydown(event) {
        const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
        if (!keys.includes(event.key)) return;

        event.preventDefault();
        const tabs = Array.from(this.tabButtons);
        const currentIndex = tabs.indexOf(event.currentTarget);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        tabs[nextIndex].focus();
        this.activateTab(tabs[nextIndex].dataset.buttonIndex);
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const index = event.target.dataset.sliderIndex;
        if (index) this.activateTab(index);
      }

      activateTab(index) {
        const activeIndex = String(index);

        this.tabButtons.forEach((button) => {
          const isActive = button.dataset.buttonIndex === activeIndex;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
          button.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.tabPanels.forEach((panel) => {
          const isActive = panel.dataset.sliderIndex === activeIndex;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });

        this.backgrounds.forEach((background) => {
          background.classList.toggle('is-active', background.dataset.sliderIndex === activeIndex);
        });

        const activeSlider = this.querySelector('[data-product-kits-panel].is-active slider-component');
        if (activeSlider && typeof activeSlider.resetPages === 'function') {
          activeSlider.resetPages();
        }

        this.syncMedia();
      }

      onMediaToggle(event) {
        event.preventDefault();
        this.userPaused = !this.userPaused;
        this.syncMedia();
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.pauseAllVideos();
          return;
        }

        this.syncMedia();
      }

      getDisplayedVideos(container) {
        if (!container) return [];

        return Array.from(container.querySelectorAll('video')).filter((video) => {
          const wrapper = video.parentElement;
          return wrapper && wrapper.offsetWidth > 0 && wrapper.offsetHeight > 0;
        });
      }

      pauseAllVideos() {
        this.querySelectorAll('video').forEach((video) => {
          video.pause();
        });
      }

      syncMedia() {
        const activeBackground = this.querySelector('[data-product-kits-background].is-active');
        const hasVideo = Boolean(activeBackground && activeBackground.querySelector('video'));
        const displayedVideos = this.getDisplayedVideos(activeBackground);

        this.pauseAllVideos();

        if (this.mediaButton) {
          this.mediaButton.hidden = !hasVideo;
          this.mediaButton.classList.toggle('is-paused', this.userPaused);
          const playLabel = this.mediaButton.dataset.playLabel;
          const pauseLabel = this.mediaButton.dataset.pauseLabel;
          this.mediaButton.setAttribute('aria-label', this.userPaused ? playLabel : pauseLabel);
          this.mediaButton.setAttribute('aria-pressed', this.userPaused ? 'false' : 'true');
        }

        if (this.userPaused || !hasVideo) return;

        displayedVideos.forEach((video) => {
          video.muted = true;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        });
      }
    }
  );
}
